import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Chip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import FlagSummary from './FlagSummary';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import ExpenseNote from './ExpenseNote';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

const ThresholdCell = ({ threshold, slabStatus }) => {
  if (slabStatus === 'no_slabs_for_department') {
    return <Chip size="small" color="warning" variant="outlined" label="No slab for this month" title="No slab is in force for this department in the selected month. Add one in Slab Configuration and set 'Applies from' to this month or earlier." />;
  }
  if (slabStatus === 'gap') {
    return <Chip size="small" color="warning" variant="outlined" label="Target is in no slab" title="This person's target falls outside every slab set for this department in the selected month - add or widen a slab." />;
  }
  if (slabStatus === 'no_target') {
    return <Typography variant="body2" color="text.secondary" title="This person has no revenue target for the month, so no slab (and no threshold) applies - they are shown but never flagged.">No target set</Typography>;
  }
  return threshold === null
    ? <Typography variant="body2" color="text.secondary">Not configured</Typography>
    : `< ${threshold}`;
};

// LCRR - Lead Cost Revenue Ratio = a person's revenue / the cost of the leads behind that revenue. Everyone with income is listed
// (revenue as the department income tracker counts it); a person with no lead cost has no LCRR and is never flagged ("if total lead
// cost = 0, do not calculate and do not warn"). The threshold comes from the Target Slab shared with TAR/ARPU/Admission Count, so
// only people with a target have one (see lcrr_dashboard.controller.js).
const LcrrDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0, needsSlabConfigCount: 0 });
  const [expenseNote, setExpenseNote] = useState(null); // why the page may look thin or empty (no marketing expense entered / expense with no leads)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/lcrr-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load LCRR results');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({
          totalEvaluated: res?.data?.totalEvaluated || 0,
          warningCount: res?.data?.warningCount || 0,
          needsSlabConfigCount: res?.data?.needsSlabConfigCount || 0,
        });
        setExpenseNote(res?.data?.expenseNote || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load LCRR results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.threshold === null || row.value === null ? -Infinity : row.threshold - row.value);
  const sortedRows = useMemo(() => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)), [rows]);

  const thresholdCsv = (row) => {
    if (row.slabStatus === 'no_slabs_for_department') return 'No slab for this month';
    if (row.slabStatus === 'gap') return 'Target is in no slab';
    if (row.slabStatus === 'no_target') return 'No target set';
    return row.threshold === null ? 'Not configured' : `< ${row.threshold}`;
  };

  const columns = [
    // no lead cost = no LCRR = not judged: a dash, not a green tick
    {
      field: 'status',
      headerName: 'Status',
      align: 'center',
      render: (row) => (row.value === null ? <Typography variant="body2" color="text.disabled" align="center">-</Typography> : <StatusCell isWarning={row.isWarning} />),
      csvValue: (row) => (row.value === null ? 'Not calculated' : row.isWarning ? 'Warning' : 'OK'),
    },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography variant="body2" fontStyle={row.isActive === false ? 'italic' : 'normal'} color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}>
          {row.entityName}{row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'revenue', headerName: 'Revenue', align: 'right', render: (row) => `₹${Number(row.revenue).toLocaleString()}`, csvValue: (row) => row.revenue },
    { field: 'leadsCount', headerName: 'Leads', align: 'right', render: (row) => <span title={`${row.enrollmentsCount} course enrollments from ${row.leadsCount} leads - each lead's cost is counted once`}>{row.leadsCount}</span>, csvValue: (row) => row.leadsCount },
    { field: 'totalLeadCost', headerName: 'Total Lead Cost', align: 'right', render: (row) => `₹${Number(row.totalLeadCost).toLocaleString()}`, csvValue: (row) => row.totalLeadCost },
    {
      field: 'value',
      headerName: 'LCRR',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          {row.value === null ? '-' : row.value}
        </Typography>
      ),
      csvValue: (row) => (row.value === null ? '-' : row.value),
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => <ThresholdCell threshold={row.threshold} slabStatus={row.slabStatus} />,
      csvValue: thresholdCsv,
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="LCRR - Lead Cost Revenue Ratio"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">Back to Warning Dashboard</Button>
          {canConfigure('marketing-expense') && (
            <>
              <Button component={Link} to="/dashboard/warning-dashboard/marketing-expense" variant="outlined">Enter Marketing Expense</Button>
              <Button component={Link} to={`/dashboard/warning-dashboard/target-slab-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">Configure Slabs</Button>
            </>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}
      {!loading && !error && <ExpenseNote note={expenseNote} month={month} canSet={canConfigure('marketing-expense')} />}
      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated}>
            {summary.needsSlabConfigCount > 0 && ` · ${summary.needsSlabConfigCount} have no target or slab, so no threshold`}
          </FlagSummary>
      )}
      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="Nobody has income this month."
          exportFileName="LCRR"
        />
      )}
    </WarningPage>
  );
};

export default LcrrDashboard;
